// @ts-expect-error -- untyped module
import PoppinsVariable from "@/assets/fonts/poppins/Poppins-VariableFont_wght.otf";

import React from "react";
import {
  View,
  Page,
  Font,
  Text,
  Document,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";
import moment from "moment";

import { ALL_POS, POS_LABEL_MAPPER } from "@/constants";

import { capitalizeInitialLetter } from "@/lib/string-util";
import type { IUserInfo } from "@/hooks/use-user-info";
import type { ICostEstFilterParams } from "@/hooks/use-cost-est-filter";
import type {
  PlaceOfService,
  ProviderCost,
  Service,
} from "@/model/provider-cost";
import { ProviderCostView } from "@/model/provider-cost";

Font.register({
  family: "Poppins",
  fonts: [
    {
      src: PoppinsVariable,
      fontWeight: "light",
    },
    {
      src: PoppinsVariable,
      fontWeight: "medium",
    },
    {
      src: PoppinsVariable,
      fontWeight: "semibold",
    },
  ],
});

type IPdfContentProps = {
  userInfo: IUserInfo;
  costEstFilterParams: ICostEstFilterParams;
  providerCost?: ProviderCost[];
};

const PDFContent: React.FunctionComponent<IPdfContentProps> = (props) => {
  // Props
  const { userInfo, costEstFilterParams, providerCost } = props;
  const logo = `data:image/png;base64,${userInfo.clientLogo}`;

  const renderHeaderSection = () => {
    return (
      <>
        {/* Header Section */}
        <View style={{ marginBottom: 14 }}>
          {/* Title */}
          <View>
            <Text style={styles.titleText}>Provider Search Results</Text>
          </View>
          {/* Header Top Section */}
          <View style={styles.headerTopSectionView}>
            {/* Member Details */}
            <View style={styles.flexOne}>
              <View style={styles.headerTopSectionRowView}>
                <Text style={styles.labelText}>Member Name: </Text>
                <Text style={styles.detailsText}>
                  {userInfo.firstname} {userInfo.lastname}
                </Text>
              </View>
              <View style={styles.headerTopSectionRowView}>
                <Text style={styles.labelText}>Group: </Text>
                <Text style={styles.detailsText}>{userInfo.groupId}</Text>
              </View>
              <View style={styles.headerTopSectionRowView}>
                <Text style={styles.labelText}>Plan: </Text>
                <Text style={styles.detailsText}>{userInfo.planId}</Text>
              </View>
              <View style={styles.headerTopSectionRowView}>
                <Text style={styles.labelText}>Run Date: </Text>
                <Text style={styles.detailsText}>
                  {moment(new Date()).format("MMMM DD, YYYY")}
                </Text>
              </View>
            </View>
            {/* Client Logo */}
            <View style={styles.logoView}>
              <Image style={styles.logo} src={logo} cache={false} />
            </View>
          </View>

          {/* Search Criteria */}
          <View style={styles.searchCriteriaView}>
            <Text style={styles.titleText}>Search Criteria:</Text>
          </View>
          <View style={styles.searchCriteriaContainerView}>
            {/* Search Criteria Section 1 */}
            <View style={styles.flexOne}>
              <View style={styles.headerTopSectionRowView}>
                <Text style={styles.labelText}>Procedure: </Text>
                <Text
                  style={styles.detailsText}
                >{`${costEstFilterParams.procedure?.description} (${costEstFilterParams.procedure?.code})`}</Text>
              </View>
              <View style={styles.headerTopSectionRowView}>
                <Text style={styles.labelText}>Distance: </Text>
                <Text
                  style={styles.detailsText}
                >{`${costEstFilterParams.distance.displayName}`}</Text>
              </View>
              <View style={styles.headerTopSectionRowView}>
                <Text style={styles.labelText}>From: </Text>
                <Text
                  style={styles.detailsText}
                >{`${costEstFilterParams.targetLocation?.description}`}</Text>
              </View>
              <View style={styles.headerTopSectionRowView}>
                <Text style={styles.labelText}>Display: </Text>
                <Text
                  style={styles.detailsText}
                >{`${costEstFilterParams.limit} results`}</Text>
              </View>
            </View>
            {/* Search Criteria Section 2 */}
            <View style={[styles.flexOne, { paddingHorizontal: 8 }]}>
              <View style={styles.headerTopSectionRowView}>
                <Text style={styles.labelText}>Facility Name/ID: </Text>
                <Text style={styles.detailsText}>
                  {costEstFilterParams.facility?.description &&
                  (costEstFilterParams.facility.code === "" ||
                    costEstFilterParams.facility.code === null ||
                    costEstFilterParams.facility.code === undefined)
                    ? `${costEstFilterParams.facility?.description} (${costEstFilterParams.facility?.code})`
                    : "N/A"}
                </Text>
              </View>
              <View style={styles.headerTopSectionRowView}>
                <Text style={styles.labelText}>Physician Name/NPI: </Text>
                <Text style={styles.detailsText}>
                  {/* {filterParams.provider === null ? "N/A" : "Y/A"} */}
                  {costEstFilterParams.provider &&
                  (costEstFilterParams.provider.code === "" ||
                    costEstFilterParams.provider.code === null ||
                    costEstFilterParams.provider.code === undefined)
                    ? `${costEstFilterParams.provider?.description} (${costEstFilterParams.provider?.code})`
                    : "N/A"}
                </Text>
              </View>
              <View style={styles.headerTopSectionRowView}>
                <Text style={styles.labelText}>Place of Service: </Text>
                <Text style={styles.detailsText}>
                  {costEstFilterParams.pos.length > 0
                    ? costEstFilterParams.pos
                        .map((pos) => pos.displayName)
                        .join(", ")
                    : ALL_POS.map((pos) => pos.displayName).join(", ")}
                </Text>
              </View>
            </View>
            {/* Search Criteria Section 3 */}
            <View style={styles.flexOne}>
              <View style={styles.headerTopSectionRowView}>
                <Text style={styles.labelText}>Network Status: </Text>
                <Text style={styles.detailsText}>In-Network</Text>
              </View>
              <View style={styles.headerTopSectionRowView}>
                <Text style={styles.labelText}>Network Tiers: </Text>
                <Text style={styles.detailsText}>
                  {costEstFilterParams.tiers.length > 0
                    ? costEstFilterParams.tiers
                        .map((tier) => tier.name)
                        .join(", ")
                    : "N/A"}
                </Text>
              </View>
              <View style={styles.headerTopSectionRowView}>
                <Text style={styles.labelText}>Network Name: </Text>
                <Text style={styles.detailsText}>
                  {costEstFilterParams.networks.length > 0
                    ? costEstFilterParams.networks
                        .map((network) => network.networkName)
                        .join(", ")
                    : "N/A"}
                </Text>
              </View>
            </View>
          </View>
          {/* Specialty */}
          <View style={styles.specialtyView}>
            <Text style={styles.labelText}>Specialty: </Text>
            <Text style={styles.detailsText}>
              {costEstFilterParams.specialities.length > 0
                ? costEstFilterParams.specialities
                    .map((specialty) => specialty.categorySpecialty)
                    .join(", ")
                : "N/A"}
            </Text>
          </View>
        </View>
      </>
    );
  };

  const renderResultSection = () => {
    return providerCost?.map((item: ProviderCost, index: number) => {
      const providerCostViewModel = new ProviderCostView(item);
      const placeholderPOS: PlaceOfService = {
        totalRate: 0,
        negotiateArrangement: "",
        services: [],
        type: "",
        bundleCodes: "",
      };
      const pointOfServices = [placeholderPOS, ...item.placeOfServices];

      return (
        <View key={index} wrap={false} style={styles.resultContainerView}>
          <View style={styles.providerNameView}>
            <Text style={styles.providerNameText}>
              {item.provider.fullName}
            </Text>
          </View>
          <View style={styles.resultsDetailView}>
            <View style={styles.providerDetailsView}>
              <View style={[styles.flexOne, { paddingHorizontal: 8 }]}>
                <View style={styles.providerDetailRowView}>
                  <Text style={styles.labelText}>Address: </Text>
                  <Text style={styles.detailsText}>{item.address}</Text>
                </View>
                <View style={styles.providerDetailRowView}>
                  <Text style={styles.labelText}>Phone Number: </Text>
                  <Text style={styles.detailsText}>{item.provider.phone}</Text>
                </View>
                <View style={styles.providerDetailRowView}>
                  <Text style={styles.labelText}>Facility Name: </Text>
                  <Text style={styles.detailsText}>
                    {item?.einDetail?.name !== null
                      ? item?.einDetail?.name
                      : "N/A"}
                  </Text>
                </View>
                <View style={styles.providerDetailRowView}>
                  <Text style={styles.labelText}>Network: </Text>
                  <Text style={styles.detailsText}>
                    {item?.networkName ? item?.networkName : "N/A"}
                  </Text>
                </View>
              </View>
              <View style={[styles.flexOne, { paddingHorizontal: 8 }]}>
                <View style={styles.providerDetailRowView}>
                  <Text style={styles.labelText}>Provider NPI: </Text>
                  <Text style={styles.detailsText}>
                    {item?.provider?.npi !== null
                      ? capitalizeInitialLetter(item?.provider?.npi)
                      : "N/A"}
                  </Text>
                </View>
                <View style={styles.providerDetailRowView}>
                  <Text style={styles.labelText}>Phone Group ID: </Text>
                  <Text style={styles.detailsText}>
                    {item?.provider?.providerGroupId !== null
                      ? capitalizeInitialLetter(item?.provider?.providerGroupId)
                      : "N/A"}
                  </Text>
                </View>
                <View style={styles.providerDetailRowView}>
                  <Text style={styles.labelText}>Provider EIN: </Text>
                  <Text style={styles.detailsText}>
                    {item?.einDetail?.id !== null ? item?.einDetail?.id : "N/A"}
                  </Text>
                </View>
                <View style={styles.providerDetailRowView}>
                  <Text style={styles.labelText}>Specialty: </Text>
                  <Text style={styles.detailsText}>
                    {item?.provider?.specialityDetails !== null &&
                    item?.provider?.specialityDetails.length > 0
                      ? item?.provider?.specialityDetails
                          ?.slice(0, 3)
                          .map((obj) => obj["description"])
                          .join(", ")
                      : "N/A"}
                  </Text>
                </View>
              </View>
            </View>

            {/* POS Section */}
            <View style={styles.posContainerView}>
              {pointOfServices.map((pos: PlaceOfService, index: number) => {
                return (
                  <View
                    key={`pos-type-${index}`}
                    style={[
                      styles.posTableView,
                      {
                        borderRightWidth:
                          index !== pointOfServices.length - 1 ? 1 : 0,
                      },
                    ]}
                  >
                    <View style={styles.posTableHeaderRowView}>
                      <Text style={styles.labelText}>
                        {POS_LABEL_MAPPER[pos.type]}
                      </Text>
                    </View>
                    <View style={styles.posTableEstimateRowView}>
                      <Text style={styles.detailsText}>
                        <Text style={styles.detailsText}>
                          {index === 0
                            ? ""
                            : pos.totalRate === null
                              ? "N/A"
                              : providerCostViewModel.placeOfServiceRates(
                                  pos.services,
                                ).estimatedTotal.rate}
                        </Text>
                      </Text>
                    </View>
                  </View>
                );
              })}
            </View>
            {/* Explanation of Total Estimate Section */}
            <View style={styles.totalEstimateHeaderView}>
              <Text style={styles.labelText}>
                Explanation of Total Estimate
              </Text>
            </View>
            <View style={styles.totalCostExplanationContainerView}>
              {pointOfServices.map((item: PlaceOfService, index: number) => {
                return (
                  <View
                    key={`explanation-of-total-estimate-${index}`}
                    style={[
                      styles.totalEstimateTableView,
                      {
                        borderRightWidth:
                          index !== pointOfServices.length - 1 ? 1 : 0,
                      },
                    ]}
                  >
                    {index === 0 ? (
                      <>
                        <View style={styles.totalEstimateTopRowView}>
                          <Text style={styles.detailsText}>Facility</Text>
                        </View>
                        <View style={styles.totalEstimateRowView}>
                          <Text style={styles.detailsText}>Professional</Text>
                        </View>
                      </>
                    ) : (
                      <>
                        {item.services.map(
                          (service: Service, index: number) => {
                            return (
                              <View
                                key={`explanation-of-total-estimate-service-${index}`}
                                style={[
                                  styles.totalEstimateView,
                                  { borderTopWidth: index !== 0 ? 1 : 0 },
                                ]}
                              >
                                <View
                                  key={`explanation-of-total-estimate-service-${index}`}
                                  style={styles.totalEstimateView}
                                >
                                  <Text style={styles.detailsText}>
                                    {/* {service?.rates?.length === 0 ||
                                      service?.rates?.some(
                                        (obj) => obj.calculatedRate === null,
                                      )
                                        ? "N/A"
                                        : `$100 Test cost`} */}
                                    {index === 0
                                      ? providerCostViewModel.placeOfServiceRates(
                                          item.services,
                                        ).facility.rate
                                      : providerCostViewModel.placeOfServiceRates(
                                          item.services,
                                        ).professional.rate}
                                  </Text>
                                </View>
                              </View>
                            );
                          },
                        )}
                      </>
                    )}
                  </View>
                );
              })}
            </View>
          </View>
        </View>
      );
    });
  };

  return (
    <Document>
      <Page size={{ width: 1142, height: 1615 }} style={styles.container}>
        {renderHeaderSection()}
        {renderResultSection()}
      </Page>
    </Document>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingVertical: 20,
    paddingHorizontal: 25,
  },
  titleText: {
    fontSize: 20,
    fontFamily: "Poppins",
    fontWeight: "semibold",
    color: "#222222",
  },
  headerTopSectionView: {
    flexDirection: "row",
    marginTop: 14,
  },
  flexOne: {
    flex: 1,
  },
  headerTopSectionRowView: {
    flexDirection: "row",
    marginBottom: 2,
    flexWrap: "wrap",
  },
  labelText: {
    fontSize: 12,
    fontFamily: "Poppins",
    fontWeight: "medium",
    color: "#222222",
  },
  detailsText: {
    fontSize: 12,
    fontFamily: "Poppins",
    fontWeight: "light",
    color: "#222222",
  },
  logoView: {
    position: "absolute",
    bottom: 6,
    right: 0,
  },
  logo: {
    objectFit: "contain",
    height: 110,
    width: 138,
  },
  searchCriteriaView: {
    marginTop: 14,
  },
  searchCriteriaContainerView: {
    marginTop: 8,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  specialtyView: {
    marginTop: 14,
    flexDirection: "row",
    flexWrap: "wrap",
  },
  resultContainerView: {
    marginTop: 24,
    borderWidth: 1,
    borderColor: "#D8D8D8",
    borderRadius: 16,
  },
  providerNameView: {
    borderBottomWidth: 1,
    borderBottomColor: "#D8D8D8",
    padding: 10,
    alignItems: "center",
  },
  providerNameText: {
    fontSize: 16,
    fontFamily: "Poppins",
    fontWeight: "semibold",
    color: "#222222",
  },
  providerDetailsView: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  providerDetailRowView: {
    flexDirection: "row",
    marginBottom: 2,
    flexWrap: "wrap",
  },
  resultsDetailView: {
    padding: 14,
  },
  posContainerView: {
    height: 80,
    marginTop: 14,
    flexDirection: "row",
    borderWidth: 1,
    borderColor: "#D8D8D8",
  },
  totalCostExplanationContainerView: {
    height: 80,
    flexDirection: "row",
    borderRightWidth: 1,
    borderLeftWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#D8D8D8",
  },
  posTableView: {
    flex: 1,
    height: 80,
    flexDirection: "column",
    borderRightColor: "#D8D8D8",
  },
  posTableHeaderRowView: {
    flex: 1,
    backgroundColor: "#EEEEEE",
    alignItems: "center",
    justifyContent: "center",
    height: 40,
  },
  posTableEstimateRowView: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    height: 40,
  },
  totalEstimateHeaderView: {
    height: 40,
    backgroundColor: "#EEEEEE",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 14,
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: "#D8D8D8",
  },
  totalEstimateTableView: {
    flex: 1,
    height: 80,
    flexDirection: "column",
    borderRightColor: "#D8D8D8",
  },
  totalEstimateTopRowView: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    height: 40,
  },
  totalEstimateRowView: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    height: 40,
    borderTopWidth: 1,
    borderColor: "#D8D8D8",
  },
  totalEstimateView: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    height: 40,
    borderColor: "#D8D8D8",
  },
});

export default PDFContent;
